import _extends from "@babel/runtime/helpers/extends";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2;
import { css } from "@emotion/core";
import styled from "@emotion/styled";
import { color } from "@smartsheet/design-tokens-react";
import { DeleteIcon, SearchIcon } from "@smartsheet/lodestar-icons";
import { warnOnce } from "@smartsheet/lodestar-utils";
import { useCombinedThemeMode } from "@smartsheet/sds-theme-internal";
import { CloseIcon, MagnifyingGlassIcon } from "@smartsheet/ursa-icons";
import React, { useCallback } from "react";
import { forwardRef } from "react";
import { neutralDark20, neutralDark30 } from "../../constants/colors";
import { focusRingTheme } from "../../util/createFocusRingStyles";
import { Clickable } from "../clickable";
import { TextInput } from "./TextInput";
import { formInputTheme } from "./styled/formInputTheme";
var StyledButton = styled("button")(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    border-style: none;\n    background-color: transparent;\n    padding: 0;\n    height: 30px;\n    width: 30px;\n    cursor: ", ";\n    // To force the height to be the children's height.\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    // This only applies to the clear button. The search icon takes Clickable's.\n    &:focus {\n        outline-color: ", ";\n        border-radius: ", ";\n    }\n"])), function (_ref) {
  var isDisabled = _ref.isDisabled;
  return isDisabled ? "default" : "pointer";
}, function (_ref2) {
  var themeMode = _ref2.themeMode;
  return focusRingTheme.outerColor(themeMode);
}, function (_ref3) {
  var themeMode = _ref3.themeMode;
  return formInputTheme.searchInputButtonRadius(themeMode);
});
var SearchIconStyles = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    // Otherwise the icon is inline-block which gives it a bit more than the specified height.\n    display: block;\n    cursor: text;\n"])));
var StyledLodestarSearchIcon = styled(SearchIcon)(SearchIconStyles);
var StyledUrsaSearchIcon = styled(MagnifyingGlassIcon)(SearchIconStyles);
var searchIcon = {
  rest: {
    "lodestar-light": /*#__PURE__*/React.createElement(StyledLodestarSearchIcon, {
      size: "small",
      color: neutralDark20
    }),
    oldestar: /*#__PURE__*/React.createElement(StyledLodestarSearchIcon, {
      size: "small",
      color: neutralDark20
    }),
    "ursa-light": /*#__PURE__*/React.createElement(StyledUrsaSearchIcon, {
      size: 24,
      color: color("surface--work--text--inactive")
    })
  },
  hover: {
    "lodestar-light": /*#__PURE__*/React.createElement(StyledLodestarSearchIcon, {
      size: "small",
      color: neutralDark30
    }),
    oldestar: /*#__PURE__*/React.createElement(StyledLodestarSearchIcon, {
      size: "small",
      color: neutralDark30
    }),
    "ursa-light": /*#__PURE__*/React.createElement(StyledUrsaSearchIcon, {
      size: 24,
      color: color("surface--work--text--inactive-hover")
    })
  }
};
var clearIcon = {
  rest: {
    "lodestar-light": /*#__PURE__*/React.createElement(DeleteIcon, {
      size: "small",
      color: neutralDark20
    }),
    oldestar: /*#__PURE__*/React.createElement(DeleteIcon, {
      size: "small",
      color: neutralDark20
    }),
    "ursa-light": /*#__PURE__*/React.createElement(CloseIcon, {
      size: 20,
      color: color("surface--work--text--inactive")
    })
  },
  hover: {
    "lodestar-light": /*#__PURE__*/React.createElement(DeleteIcon, {
      size: "small",
      color: neutralDark30
    }),
    oldestar: /*#__PURE__*/React.createElement(DeleteIcon, {
      size: "small",
      color: neutralDark30
    }),
    "ursa-light": /*#__PURE__*/React.createElement(CloseIcon, {
      size: 20,
      color: color("surface--work--text--inactive-hover")
    })
  }
};
var warnOnDeprecatedLeftIconButtonProps = warnOnce("Please use the `contentBeforeButtonProps` prop for <TextInput/> instead of `leftIconButtonProps`. The `leftIconButtonProps` prop has been deprecated for better RTL better support.");

// Reserving namespace for consistency.

var ClearSearchButton = function ClearSearchButton(_ref4) {
  var isDisabled = _ref4.isDisabled,
    isHovered = _ref4.isHovered,
    onClick = _ref4.onClick,
    value = _ref4.value;
  var themeMode = useCombinedThemeMode();
  var onClickedCallback = useCallback(function (e) {
    if (isDisabled) {
      return;
    }
    onClick(e);
  }, [isDisabled, onClick]);
  if (value === "") {
    return null;
  }
  var IconComponent = isHovered ? clearIcon.hover[themeMode] : clearIcon.rest[themeMode];
  return /*#__PURE__*/React.createElement(StyledButton, {
    isDisabled: isDisabled,
    "aria-label": "clear",
    type: "button",
    themeMode: themeMode,
    onClick: onClickedCallback
  }, IconComponent);
};
export var SearchInput = /*#__PURE__*/forwardRef(function (props, parentRef) {
  // TODO: remove this after deprecation in TextInput, use just the `isDisabled` prop.
  var reallyDisabled = props.isDisabled !== undefined ? props.isDisabled : props.disabled ? props.disabled : false;
  var themeMode = useCombinedThemeMode();

  // TODO: Remove this in https://git.lab.smartsheet.com/team-ui-engineering/lodestar-core/-/issues/516
  var contentAfterButtonProps = props.contentAfterButtonProps,
    contentBeforeButtonProps = props.contentBeforeButtonProps,
    leftIconButtonProps = props.leftIconButtonProps,
    onChange = props.onChange,
    value = props.value;
  var realContentBeforeButtonProps = contentBeforeButtonProps !== undefined ? contentBeforeButtonProps : leftIconButtonProps;
  var contentAfterButtonClick = contentAfterButtonProps === null || contentAfterButtonProps === void 0 ? void 0 : contentAfterButtonProps.onClick;
  if (process.env.NODE_ENV !== "production") {
    if (leftIconButtonProps !== undefined) {
      warnOnDeprecatedLeftIconButtonProps();
    }
  }
  return /*#__PURE__*/React.createElement(TextInput, _extends({
    contentBefore: function contentBefore(_ref5) {
      var isHovered = _ref5.isHovered;
      var icon = isHovered ? searchIcon.hover[themeMode] : searchIcon.rest[themeMode];
      if (realContentBeforeButtonProps !== undefined) {
        return /*#__PURE__*/React.createElement(Clickable, _extends({}, realContentBeforeButtonProps, {
          focusRingOptions: {
            colorOverride: focusRingTheme.outerColor(themeMode),
            radius: formInputTheme.searchInputButtonRadius(themeMode)
          }
        }), icon);
      } else {
        return icon;
      }
    },
    contentAfter: function contentAfter(_ref6) {
      var isHovered = _ref6.isHovered;
      return /*#__PURE__*/React.createElement(ClearSearchButton, {
        value: value,
        isDisabled: reallyDisabled,
        isHovered: isHovered,
        onClick: function onClick(e) {
          onChange("", e);
          if (contentAfterButtonClick != null) {
            contentAfterButtonClick(e);
          }
        }
      });
    },
    ref: parentRef
  }, props));
});
SearchInput.displayName = "SearchInput";
//# sourceMappingURL=SearchInput.js.map